/**
 * Error Messages Utility
 * Centralized error and success messages for the Inbox V2 component
 */
import { message } from 'antd';

export const errorMessages = {
  sendMessage: 'Failed to send message. Please try again.',
  loadConversations: 'Failed to load conversations. Please refresh the page.',
  loadThreads: 'Failed to load messages. Please try again.',
  deleteContact: 'Failed to delete contact. Please try again.',
  addTag: 'Failed to add tag. Please try again.',
  removeTag: 'Failed to remove tag. Please try again.',
  starContact: 'Failed to update contact. Please try again.',
  downloadPDF: 'Failed to download PDF. Please try again.',
  invalidPhoneNumber: 'Contact has an invalid phone number.',
  noTextingNumber: 'No texting number configured. Please contact support.',
  networkError: 'Network error. Please check your connection.',
  shortenUrl: 'Failed to shorten URL. Please try again.',
  deleteScheduledMessage: 'Failed to delete scheduled message. Please try again.',
  calendarInvite: 'Failed to send calendar invite. Please try again.',
  markAsRead: 'Failed to mark conversation as read.',
  sequencePreview: 'Failed to load sequence. Please try again.',
  addGroup: 'Failed to add group. Please try again.',
  removeGroup: 'Failed to remove group. Please try again.',
  createGroup: 'Failed to create group. Please try again.',
  loadGroups: 'Failed to load groups. Please try again.',
  archiveContact: 'Failed to update archive. Please try again.',
  calendarCheck: 'Your calendar may need to be reconnected. If this issue persists, contact support.',
  // ADR-025 WhatsApp channel
  noWhatsAppNumber: 'No WhatsApp sender configured for this company.',
  whatsappWindowClosed: 'WhatsApp window closed. Choose how to send.',
};

export const successMessages = {
  messageSent: 'Message sent successfully',
  tagAdded: 'Tag added successfully',
  tagRemoved: 'Tag removed successfully',
  contactStarred: 'Contact starred',
  contactUnstarred: 'Contact unstarred',
  scheduledMessageDeleted: 'Scheduled message deleted',
  pdfDownloaded: 'PDF download started',
  calendarInviteSent: 'Calendar invite sent successfully',
  urlShortened: 'URL shortened successfully',
  sequenceLoaded: 'Sequence loaded — review and send to start the flow',
  groupAdded: 'Contact added to group',
  groupRemoved: 'Contact removed from group',
  groupCreated: 'Group created and contact added',
  conversationArchived: 'Conversation archived',
  conversationUnarchived: 'Conversation moved back to Inbox',
  // ADR-025 WhatsApp channel
  sentWhatsApp: 'Sent on WhatsApp',
  sentSmsFallback: 'WhatsApp window closed — sent as SMS',
  sentTemplate: 'Sent as WhatsApp template',
  heldForReply: 'Held — will send when the candidate replies',
  sentTemplateHeld: 'Sent as WhatsApp template · your message is held until they reply',
};

/**
 * Show an error notification to the user
 */
export const showError = (errorKey: keyof typeof errorMessages, customMessage?: string) => {
  message.error(customMessage || errorMessages[errorKey]);
};

/**
 * Show a success notification to the user
 */
export const showSuccess = (successKey: keyof typeof successMessages, customMessage?: string) => {
  message.success(customMessage || successMessages[successKey]);
};

/**
 * Show a loading notification that can be dismissed
 */
export const showLoading = (loadingMessage: string) => {
  return message.loading(loadingMessage, 0); // 0 means it won't auto-dismiss
};

/**
 * Show a warning notification to the user
 */
export const showWarning = (warningMessage: string) => {
  message.warning(warningMessage);
};

/**
 * Show an info notification to the user
 */
export const showInfo = (infoMessage: string) => {
  message.info(infoMessage);
};
